/**
 * Pathway-related constants
 */

// Default fallback image for pathways when no image is provided
export const DEFAULT_PATHWAY_IMAGE =
  'https://assets.ea.asu.edu/ea/img/pathways/Business/BAappliedbusinessandtechnologysolutions.png';

// Pathway states from the my-pathway API
export const PATHWAY_STATE = {
  NEW: 'new',
  RECOMMENDATIONS: 'recommendations',
  ENROLLED: 'enrolled'
};

// Badge colors for pathway courses
export const PATHWAY_BADGE_COLORS = {
  ENROLLED: '#ffeebe',
  REQUIRED: '#e74973',
  RECOMMENDED: '#ffd152',
  COMPLETED: '#c8e6c9',
  ENROLL_AND_PAY: '#fff3e0'
};

// ULC-2919 — Corporate partners that should not see on-campus pathway offerings.
// For these partners the pathway "location" is shown as online-only: any
// "On Campus" portion is stripped from the displayed location string, unless
// the pathway is offered only on campus.
//
// Gating is by an explicit partner-name list (not partner_type): the in-scope
// corporate partners share partner_type values with existing pathway-enabled
// internal partners (e.g. "Corp - Other", "Organic - *"), so a type-based gate
// would wrongly strip on-campus from those internal partners. This list is the
// live recreation of the "Exclude on campus for UP Corp partners" SF report
// (Product = Universal Learner Courses, Is Pathways Allowed = false, Partner Type
// NOT IN the internal/non-CP types), union of prod + QA so it matches either
// environment's partner naming. See ULC-2919 / asu/ulc-2919/report.soql.
export const ON_CAMPUS_HIDDEN_PARTNERS = [
  'ASU Admitted Students',
  'Adidas',
  'Adidas Family',
  'Advocate Health',
  'Amazon Career Choice',
  'Amazon DSP (ULC)',
  'Aramark',
  'Ascension (ULC)',
  'Carvana',
  'Chick-fil-A',
  'Desert Financial Credit Union',
  'Desert Financial Credit Union - Member Benefit',
  'Giant Eagle (Pathways)',
  'Google IT Certification',
  'Graduate Hotels',
  'Hospital Sisters Health System',
  'InStride (ULC)',
  'InStride Family (ULC)',
  'Intel (FSE)',
  'Intermountain Healthcare',
  'Intermountain Healthcare (ULC)',
  'Intermountain Healthcare Family (ULC)',
  'KFC Foundation',
  'LabCorp',
  'Magna International',
  'Major League Baseball',
  'Medtronic',
  'Medtronic (ULC)',
  'NBA G League',
  'NBA G League Pro Path',
  'Novant Health  Family (ULC)',
  'Novant Health (ULC)',
  'Organic Homeschool Learners',
  'Pacific Dental Services (PDS Health)',
  'Piston Group',
  'Prime Communications',
  'Propel America',
  'Rochester Regional Health',
  'SBUX SFL Partners',
  'SSM Health',
  "Saint Peter's Healthcare",
  'Starbucks EMEA Partners',
  'Starbucks Partners',
  'Taco Bell',
  'Taco Bell (ULC)',
  'Taco Bell Foundation',
  'Takeda',
  'Tribal EM',
  'Tribal Health',
  'Tribal Health Family',
  'UFCW99',
  'Uber',
  'Uber Eats',
  'Uber Eats Family',
  'Uber Family',
  'WinCo Foods'
];

const ON_CAMPUS_HIDDEN_PARTNER_SET = new Set(ON_CAMPUS_HIDDEN_PARTNERS);

// Whether the given partner should have "On Campus" hidden from pathway locations.
export const isOnCampusHiddenPartner = (partnerName) => ON_CAMPUS_HIDDEN_PARTNER_SET.has(partnerName);

// Strip the "On Campus" portion from a pathway location string.
//   'Online, On Campus' -> 'Online'
//   'On Campus'         -> 'On Campus' (an on-campus-only program is never shown as online)
//   'Online'            -> 'Online'
export const stripOnCampusLocation = (location) => {
  if (!location) return location;
  const stripped = location
    .replace(/\s*,?\s*On Campus\s*,?\s*/gi, '')
    .replace(/^\s*,\s*/, '')
    .trim();
  return stripped || location;
};

// Engineering degree pathways that carry additional admission + prerequisite requirements.
// Gated by explicit name because the Engineering interest area also includes pathways not in scope.
export const ENGINEERING_ADDITIONAL_REQUIREMENTS_PATHWAYS = [
  'Aerospace Engineering, BSE',
  'Biomedical Engineering, BSE',
  'Chemical Engineering, BSE',
  'Civil Engineering, BSE',
  'Computer Science, BS',
  'Computer Systems Engineering, BSE',
  'Construction Engineering, BSE',
  'Construction Management and Technology, BS',
  'Electrical Engineering, BSE',
  'Electrical Engineering: (Electric Power and Energy Systems), BSE',
  'Engineering Management, BSE',
  'Environmental Engineering, BSE',
  'Industrial Engineering, BSE',
  'Informatics, BS',
  'Materials Science and Engineering, BSE',
  'Mechanical Engineering, BSE',
  'Software Engineering, BS'
];

const ENGINEERING_ADDITIONAL_REQUIREMENTS_PATHWAY_SET = new Set(ENGINEERING_ADDITIONAL_REQUIREMENTS_PATHWAYS);

// Notice appended to the bottom of the Admission Requirements section for the pathways above.
// Uses only tags/attrs the sidebar's sanitizeHtml allows; the sidebar CSS renders the link maroon.
export const ENGINEERING_ADDITIONAL_REQUIREMENTS_NOTICE_HTML =
  '<p><strong>This engineering pathway includes additional admission and prerequisite requirements. ' +
  'Contact our <a href="https://ea.asu.edu/help-center/" target="_blank" rel="noopener noreferrer">support team</a> ' +
  'if you need help understanding your next steps.</strong></p>';

// Whether the given pathway should show the additional-requirements notice.
export const isEngineeringAdditionalRequirementsPathway = (pathwayName) =>
  ENGINEERING_ADDITIONAL_REQUIREMENTS_PATHWAY_SET.has(pathwayName);

export default {
  DEFAULT_PATHWAY_IMAGE,
  PATHWAY_STATE,
  PATHWAY_BADGE_COLORS,
  ON_CAMPUS_HIDDEN_PARTNERS,
  isOnCampusHiddenPartner,
  stripOnCampusLocation,
  ENGINEERING_ADDITIONAL_REQUIREMENTS_PATHWAYS,
  ENGINEERING_ADDITIONAL_REQUIREMENTS_NOTICE_HTML,
  isEngineeringAdditionalRequirementsPathway
};
